/**
 * MapPicker - Componente de seleção de localização via mapa
 * Permite clicar no mapa para selecionar coordenadas
 * Inclui busca de endereço com autocomplete
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Map, { Marker, NavigationControl, GeolocateControl, ViewStateChangeEvent, GeolocateResultEvent, MapRef } from 'react-map-gl';
import { FaMapMarkerAlt, FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import 'mapbox-gl/dist/mapbox-gl.css';
import './MapPicker.css';


// Mapbox token - deve ser configurado no .env
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

export interface LocationData {
  latitude: number;
  longitude: number;
  address?: string;
}

interface MapPickerProps {
  initialLocation?: LocationData;
  onLocationSelect: (location: LocationData) => void;
  onClose?: () => void;
  height?: string | number;
  showSearch?: boolean;
}

interface SearchSuggestion {
  id: string;
  place_name: string;
  center: [number, number];
}

const MapPicker: React.FC<MapPickerProps> = ({
  initialLocation,
  onLocationSelect,
  onClose,
  height = 400,
  showSearch = true,
}) => {
  console.log('🗺️ MapPicker renderizando - initialLocation:', initialLocation);
  
  // Estado do viewport
  const [viewState, setViewState] = useState({
    longitude: initialLocation?.longitude || -46.6333,
    latitude: initialLocation?.latitude || -23.5505,
    zoom: initialLocation ? 15 : 12,
  });

  // Referência do mapa
  const mapRef = useRef<MapRef>(null);

  // Estado de carregamento do mapa
  const [mapReady, setMapReady] = useState(false);

  // Fallback: se o mapa não carregar em 5 segundos, assume que está pronto
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!mapReady) {
        console.log('🗺️ MapPicker - Timeout de carregamento, assumindo mapa pronto');
        setMapReady(true);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [mapReady]);

  // Marcador selecionado
  const [marker, setMarker] = useState<{ lng: number; lat: number } | null>(
    initialLocation
      ? { lng: initialLocation.longitude, lat: initialLocation.latitude }
      : null
  );

  // Busca de endereço
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Fecha sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Busca sugestões de endereço (autocomplete)
  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
        `access_token=${MAPBOX_TOKEN}&country=br&language=pt&types=address,place,locality&limit=5`
      );
      const data = await response.json();
      setSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erro ao buscar endereço:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce na busca
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(query);
    }, 300);
  };

  // Seleciona sugestão
  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    const [lng, lat] = suggestion.center;
    
    setMarker({ lng, lat });
    setViewState(prev => ({
      ...prev,
      longitude: lng,
      latitude: lat,
      zoom: 16,
    }));
    setSearchQuery(suggestion.place_name);
    setShowSuggestions(false);
    
    onLocationSelect({
      latitude: lat,
      longitude: lng,
      address: suggestion.place_name,
    });
  };

  // Clique no mapa para selecionar localização
  const handleMapClick = async (event: any) => {
    const { lngLat } = event;
    const lng = lngLat.lng;
    const lat = lngLat.lat;

    console.log('🗺️ MapPicker - Clique no mapa:', { lat, lng });
    setMarker({ lng, lat });

    // Geocodificação reversa para obter o endereço
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?` +
        `access_token=${MAPBOX_TOKEN}&language=pt&types=address,place`
      );
      const data = await response.json();
      const address = data.features?.[0]?.place_name || '';

      console.log('🗺️ MapPicker - Chamando onLocationSelect:', { latitude: lat, longitude: lng, address });
      onLocationSelect({
        latitude: lat,
        longitude: lng,
        address,
      });
    } catch (error) {
      console.log('🗺️ MapPicker - Chamando onLocationSelect (sem endereço):', { latitude: lat, longitude: lng });
      onLocationSelect({
        latitude: lat,
        longitude: lng,
      });
    }
  };

  // Handler para arrastar o marcador
  const handleMarkerDrag = async (event: any) => {
    const { lngLat } = event;
    const lng = lngLat.lng;
    const lat = lngLat.lat;

    console.log('🗺️ MapPicker - Marcador arrastado:', { lat, lng });
    setMarker({ lng, lat });

    // Geocodificação reversa para obter o endereço
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?` +
        `access_token=${MAPBOX_TOKEN}&language=pt&types=address,place`
      );
      const data = await response.json();
      const address = data.features?.[0]?.place_name || '';

      onLocationSelect({
        latitude: lat,
        longitude: lng,
        address,
      });
    } catch (error) {
      onLocationSelect({
        latitude: lat,
        longitude: lng,
      });
    }
  };

  // Limpa busca
  const handleClearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div className="map-picker-error">
        <FaMapMarkerAlt />
        <p>Token do Mapbox não configurado</p>
        <small>Configure REACT_APP_MAPBOX_TOKEN no arquivo .env</small>
      </div>
    );
  }

  return (
    <div className="map-picker-container" style={{ height }}>
      {/* Overlay de carregamento - bloqueia eventos de mouse até o mapa estar pronto */}
      {!mapReady && (
        <div className="map-loading-overlay">
          <FaSpinner className="spinner" />
        </div>
      )}

      {/* Barra de busca */}
      {showSearch && (
        <div className="map-search-container" ref={searchContainerRef}>
          <div className="map-search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="map-search-input"
              placeholder="Buscar endereço..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            />
            {isSearching && <FaSpinner className="loading-icon spinner" />}
            {searchQuery && !isSearching && (
              <button type="button" className="clear-search-button" onClick={handleClearSearch}>
                <FaTimes />
              </button>
            )}
          </div>

          {/* Sugestões */}
          {showSuggestions && suggestions.length > 0 && (
            <ul className="map-search-suggestions">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <FaMapMarkerAlt className="suggestion-icon" />
                  <span>{suggestion.place_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Mapa */}
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt: ViewStateChangeEvent) => setViewState(evt.viewState)}
        onClick={handleMapClick}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        cursor="crosshair"
        attributionControl={false}
      >
        <NavigationControl position="top-right" />
        <GeolocateControl
          position="top-right"
          trackUserLocation
          onGeolocate={(position: GeolocateResultEvent) => {
            const { longitude, latitude } = position.coords;
            setMarker({ lng: longitude, lat: latitude });
            onLocationSelect({ latitude, longitude });
          }}
        />

        {/* Marcador - renderiza quando há marker definido */}
        {marker && (
          <Marker
            longitude={marker.lng}
            latitude={marker.lat}
            anchor="bottom"
            draggable
            onDragEnd={handleMarkerDrag}
          >
            <div className="map-marker draggable">
              <FaMapMarkerAlt size={32} color="#E53935" />
            </div>
          </Marker>
        )}
      </Map>

      {/* Instrução */}
      <div className="map-instruction">
        <FaMapMarkerAlt />
        <span>Clique no mapa ou arraste o marcador para ajustar a localização</span>
      </div>

      {/* Botão fechar (se houver callback) */}
      {onClose && (
        <button type="button" className="map-close-button" onClick={onClose}>
          <FaTimes />
        </button>
      )}
    </div>
  );
};

export default MapPicker;
